import moment from "moment";

import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItemMedicationAndPrescription } from "./MedicalItemMedicationAndPrescription";

class MedicalItemModel {
    public static sortByCreationDate<T extends MedicalItem>(x: T[]): T[] {
        return x
            .sortByProperty(x => x.Id) // Sometimes all the dates are equal .... order by Id to have the same result with backend
            .sortByProperty(x => x.PharmaStartDate)
            .sortByProperty(x => x.CommitDate == null ? "3000-01-01" : x.CommitDate) // If a commit date is null its considered really recent
            .sortByProperty(x => x.ConsultationDate)
            .sortByProperty(x => MedicalItemModel.getMedicalItemCreationDate(x));
    }

    public static sortByCreationDateDesc<T extends MedicalItem>(x: T[]): T[] {
        return MedicalItemModel.sortByCreationDate(x).reverse();
    }

    public static getMedicalItemCreationDate(x: MedicalItem): Date {
        if (x.EMRTemplateMedicalItemType == MedicalItemTypeEnum.MedicationsAndPrescriptions)
            return MedicalItemModel.getMedicationCreationDate(x as MedicalItemMedicationAndPrescription);

        if (x.CreationDate != null) return x.CreationDate; // since 2019-08-26
        else if (x.ConsultationDate != null) return x.ConsultationDate;
        else if (x.CommitDate != null) return x.CommitDate;
        else if (x.PharmaStartDate != null) return x.PharmaStartDate; // Medication only have Pharma

        else return moment("1990-01-01", "YYYY-MM-DD").toDate(); // Fallback date
    }

    // Returns the latest problemItem based on creationDate or pharmaStartDate or consultationDate or commitDate or...
    public static getMedicalItemEffectiveDate(x: MedicalItem): Date {
        if (x.EMRTemplateMedicalItemType == MedicalItemTypeEnum.VitalSigns)
            return MedicalItemModel.getVitalSignEffectiveDate(x);
        if (x.EMRTemplateMedicalItemType == MedicalItemTypeEnum.MedicationsAndPrescriptions)
            return MedicalItemModel.getMedicationEffectiveDate(x as MedicalItemMedicationAndPrescription);

        if (x.CreationDate != null) return x.CreationDate;
        else if (x.PharmaStartDate != null) return x.PharmaStartDate;
        else if (x.ConsultationDate != null) return x.ConsultationDate;
        else if (x.CommitDate != null) return x.CommitDate;
        else return moment("1990-01-01", "YYYY-MM-DD").toDate(); // Fallback date
    }

    public static getMedicationCreationDate(x: MedicalItemMedicationAndPrescription): Date {
        if (x.CreationDate != null) return x.CreationDate; // since 2019-08-26
        else if (x.PharmaStartDate != null) return x.PharmaStartDate; // <-- Is the Creation Date before 2019-08-26
        else if (x.ConsultationDate != null) return x.ConsultationDate;
        else if (x.CommitDate != null) return x.CommitDate;
        else return moment("1990-01-01", "YYYY-MM-DD").toDate(); // Fallback date
    }

    public static getMedicationEffectiveDate(x: MedicalItemMedicationAndPrescription): Date {
        if (x.RefillStartDate != null) return x.RefillStartDate;
        else if (x.CreationDate != null) return x.CreationDate; // since 2019-08-26
        else if (x.PharmaStartDate != null) return x.PharmaStartDate; // <-- Is the Creation Date before 2019-08-26
        else if (x.ConsultationDate != null) return x.ConsultationDate;
        else if (x.CommitDate != null) return x.CommitDate;
        else return moment("1990-01-01", "YYYY-MM-DD").toDate(); // Fallback date
    }

    // For Vitals when looking across All EMRTemplate we want the last prelevement date not the last item created. Since, VitalSign, we display 1 vital for All EMRTemplate and not 1 per EMRTemaplte
    public static getVitalSignEffectiveDate(x: MedicalItem): Date {
        if (x.PharmaStartDate != null) return x.PharmaStartDate;
        else if (x.ConsultationDate != null) return x.ConsultationDate;
        else if (x.CreationDate != null) return x.CreationDate;
        else if (x.CommitDate != null) return x.CommitDate;
        else return moment("1990-01-01", "YYYY-MM-DD").toDate(); // Fallback date
    }

    public static getMedicalItemTypeLabel(medicalItemType: MedicalItemTypeEnum): string {
        switch (medicalItemType) {
            case MedicalItemTypeEnum.SocialHabitsAndRiskFactors:
                return cacheStore.GetString("SocialHabitsLabel");
            case MedicalItemTypeEnum.AlertsAndSpecialNeeds:
                return cacheStore.GetString("AlertsAndSpecialNeedsLabel");
            case MedicalItemTypeEnum.FamilyHistory:
                return cacheStore.GetString("FamilyHistoryLabel");
            case MedicalItemTypeEnum.Immunization:
                return cacheStore.GetString("ImmunizationsLabel");
            case MedicalItemTypeEnum.MedicationsAndPrescriptions:
                return cacheStore.GetString("ActiveMedicationLabel");
            case MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions:
                return cacheStore.GetString("ProblemesActifsLabel");
            case MedicalItemTypeEnum.MedicalReaction:
                return cacheStore.GetString("AllergiesAndIntoleranceLabel");
            case MedicalItemTypeEnum.VitalSigns:
                return cacheStore.GetString("VitalSignsLabel");
            case MedicalItemTypeEnum.ScreeningTest:
                return cacheStore.GetString("PreventiveActivityLabel");
            default:
                return "";
        }
    }

    public static getMedicalItemSubTypeLabel(medicalItemSubType: MedicalItemSubtypeEnum): string {
        let medicalItemSubTypeLabel = "";

        switch (medicalItemSubType) {
            case MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing: {
                medicalItemSubTypeLabel = cacheStore.GetString("ProblemesActifsLabel");
            }
                break;
            case MedicalItemSubtypeEnum.ProblemAndCondition_Past: {
                medicalItemSubTypeLabel = cacheStore.GetString("AntecedentsMedicauxLabel");
            }
                break;
            case MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast: {
                medicalItemSubTypeLabel = cacheStore.GetString("AntecedentsChirurgicauxLabel");
            }
                break;
            case MedicalItemSubtypeEnum.ProblemAndCondition_Psychiatric: {
                medicalItemSubTypeLabel = cacheStore.GetString("AntecedentsPsychiatriquesLabel");
            }
                break;
            case MedicalItemSubtypeEnum.ProblemAndCondition_Gynecology: {
                medicalItemSubTypeLabel = cacheStore.GetString("AntecedentsObstetriquesLabel");
            }
                break;
        }
        return medicalItemSubTypeLabel;
    }
}

interface IMedicalItem {
    getMedicalItem(): MedicalItem

}

interface ISortableMedicalItem {
    EMRTemplateMedicalItemType?: MedicalItemTypeEnum;
    EMRTemplateMedicalItemSubType?: MedicalItemSubtypeEnum;
    EMRTemplateSortIndex: number;
}

class MedicalItem implements ISortableMedicalItem {
    public IsNewMedicalItem: boolean;
    public IsNewEMRTemplate: boolean;

    public Id: Guid;
    public Version: number;
    public CreationDate: Date;
    public CreatedBy: string;
    public LastUpdateDate: Date;
    public LastUpdatedBy: string;
    public DeletionDate: Date;
    public DeletedBy: string;
    public ArchivalDate: Date;
    public ArchivedBy: string;
    public Notes: string;
    public IsCommitted: boolean;
    public CommitDate: Date;
    public StartDateLifeStageId: string;
    public ContextLookupId: string;
    public EmployeeId: string;
    public SigningEmployeeEmployeeId: string;
    public EMRTemplateId: string;
    public HideDate: Date;
    public PharmaStartDate: Date;
    public ConsultationDate: Date;
    public RelatedEncounterId?: Guid;
    public EMRTemplateMedicalItemType: MedicalItemTypeEnum;
    public EMRTemplateMedicalItemSubType: MedicalItemSubtypeEnum;
    public EMRTemplateSortIndex: number;
    public EMRTemplateIsInvalid: boolean;
    public EMRTemplateIsVisibleInCPP: boolean;
    public EMRTemplateIsDisplayPopup: boolean;
    public EMRTemplateIsVisibleOnExport: boolean;
    public EMRTemplatePrescriptionId: Guid;
    public MedicalItemId: Guid;
    public LifeStageMode: LifeStageModeEnum;
    public LifeStageDate: Date;
    public LifeStagePrecisionDate: LifeStageDatePrecisionEnum;
    public LifeStageAge: number;
    public LifeStageAgeStatusLookupId: string;
    public LifeStageStatusLookupId: string;
    public IsCollapsed: boolean;
    public DisplayAsInvalid: boolean;
    public displayId: string;

    constructor(init?: Partial<MedicalItem>) {
        Object.assign(this, init);
    }

    public static newItem(encounterId: Guid, emrMedicalItemType: MedicalItemTypeEnum): MedicalItem {
        const medicalItemId = GuidHelper.Empty();
        return new MedicalItem({
            EMRTemplateId: GuidHelper.Empty(),
            EMRTemplateMedicalItemType: emrMedicalItemType,
            displayId: sessionStore.newId().toString(),
            EMRTemplateIsInvalid: false,
            EMRTemplateIsVisibleInCPP: true,
            EMRTemplateIsVisibleOnExport: true,
            CommitDate: new Date(),
            IsNewMedicalItem: true,
            RelatedEncounterId: encounterId,
            EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
            MedicalItemId: medicalItemId,
            Id: medicalItemId,
            IsCommitted: false,
            IsNewEMRTemplate: true
        });
    }
}

class MedicalItemEditContext {
    public selectedMedicalProblemSubType?: MedicalItemSubtypeEnum;
    public isMedicalItemFromOutsideNote?: boolean;
    public isCreatingNewMedicalItem?: boolean;
    public medicalItemPreviousRoute?: LocalPatientOperations.MedicalNote;
}

export { IMedicalItem, ISortableMedicalItem, MedicalItem, MedicalItemEditContext, MedicalItemModel };
